import { FC } from 'react'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import ResponsiveImage from '@/components/atoms/responsiveImageNew'
import { useDefaultTrendingSearches } from './trendingAiSearches'
import { Clock3Icon, SearchIcon, SparklesIcon, XIcon } from './aiSearchIcons'

const EXAMPLE_TONES = ['#edf8f5', '#fbf0f6', '#f3f6e8', '#edf8f5']

interface AiSearchDiscoveryProps {
  recentSearches: string[]
  onSearch: (value: string) => void
  onRemoveRecent: (value: string) => void
  onClose?: () => void
  isWide?: boolean
}

const AiSearchDiscovery: FC<AiSearchDiscoveryProps> = ({
  recentSearches,
  onSearch,
  onRemoveRecent,
  onClose,
  isWide
}) => {
  const { t } = useTranslation('common')
  const examples = useDefaultTrendingSearches()

  return (
    <div data-wide={isWide}>
      <StyledIntro>
        <SparklesIcon />
        <div>
          <strong>{t('Toolbar.AiSearch.cardAction')}</strong>
          <p>{t('Toolbar.AiSearch.introText')}</p>
        </div>
        {onClose && (
          <StyledCloseButton
            type="button"
            aria-label={t('Toolbar.AiSearch.collapse')}
            onClick={onClose}
          >
            <XIcon />
          </StyledCloseButton>
        )}
      </StyledIntro>
      <StyledSection>
        <StyledSectionHeading>
          <h2>{t('Toolbar.AiSearch.examplesTitle')}</h2>
          <span>{t('Toolbar.AiSearch.tapToSearch')}</span>
        </StyledSectionHeading>
        <StyledCards>
          {examples.map((example, index) => (
            <StyledExample
              key={example.label}
              type="button"
              onClick={() => onSearch(example.label)}
            >
              <StyledPhrase
                style={{
                  background: EXAMPLE_TONES[index % EXAMPLE_TONES.length]
                }}
              >
                “{example.label}”
              </StyledPhrase>
              <StyledExampleImage>
                <ResponsiveImage
                  imageUrl={example.imageUrl}
                  layout="fill"
                  objectFit="contain"
                  alt={example.label}
                />
              </StyledExampleImage>
              <StyledExampleAction>
                <SearchIcon />
                {t('Toolbar.AiSearch.exampleAction')}
              </StyledExampleAction>
            </StyledExample>
          ))}
        </StyledCards>
      </StyledSection>
      {recentSearches.length > 0 && (
        <StyledSection>
          <StyledHeading>{t('Toolbar.AiSearch.recentTitle')}</StyledHeading>
          <StyledRecentGrid>
            {recentSearches.map((search) => (
              <StyledRecentRow key={search}>
                <StyledRecentButton
                  type="button"
                  onClick={() => onSearch(search)}
                >
                  <Clock3Icon />
                  <span>{search}</span>
                </StyledRecentButton>
                <StyledRemoveButton
                  type="button"
                  aria-label={search}
                  onClick={() => onRemoveRecent(search)}
                >
                  <XIcon />
                </StyledRemoveButton>
              </StyledRecentRow>
            ))}
          </StyledRecentGrid>
        </StyledSection>
      )}
    </div>
  )
}

export default AiSearchDiscovery

const StyledIntro = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 20px;
  color: #787881;
  font-size: 12px;
  line-height: 1.5;

  > svg {
    flex-shrink: 0;
    width: 17px;
    height: 17px;
    margin-top: 1px;
    color: #b17099;
  }

  strong {
    display: block;
    color: #45454e;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
  }

  p {
    margin: 4px 0 0;
  }

  > div {
    flex: 1;
    min-width: 0;
  }
`

const StyledCloseButton = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  margin: 2px 0 0 4px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #242428;
  color: #fff;
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
  }
`

const StyledSection = styled.section`
  margin-top: 24px;
`

const StyledHeading = styled.h2`
  margin: 0 0 12px;
  color: #242428;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.3px;
`

const StyledSectionHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 13px;

  h2 {
    margin: 0;
    color: #242428;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.3px;
  }

  span {
    color: #888891;
    font-size: 11px;
  }
`

const StyledCards = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;

  [data-wide='true'] & {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    overflow: visible;
  }
`

const StyledExample = styled.button`
  display: flex;
  flex-direction: column;
  flex: 0 0 170px;
  box-sizing: border-box;
  scroll-snap-align: start;
  padding: 0;
  border: 1px solid #ececef;
  border-radius: 15px;
  overflow: hidden;
  background: #fff;
  color: #303037;
  text-align: center;
  cursor: pointer;

  [data-wide='true'] & {
    min-width: 0;
  }
`

const StyledPhrase = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: 96px;
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;

  [data-wide='true'] & {
    font-size: 13px;
  }
`

const StyledExampleImage = styled.span`
  position: relative;
  display: block;
  height: 162px;
  margin: 8px;
  background: #fff;

  [data-wide='true'] & {
    height: 152px;
  }
`

const StyledExampleAction = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  background: #fafafb;
  border-top: 1px solid #f1f1f3;
  color: #666670;
  font-size: 12px;

  svg {
    width: 16px;
    height: 16px;
  }
`

const StyledRecentGrid = styled.div`
  [data-wide='true'] & {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 24px;
  }
`

const StyledRecentRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f2;

  svg {
    display: block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: #9999a2;
  }
`

const StyledRecentButton = styled.button`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 10px;
  min-width: 0;
  min-height: 46px;
  padding: 5px 0;
  border: 0;
  background: transparent;
  color: #666670;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const StyledRemoveButton = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #686873;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    color: inherit;
  }
`
